import { type ToastProps, useConstant, useEffectOnce, useToasts } from '@talos/kyoko';
import { createContext, type PropsWithChildren, useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useAppStateSelector } from '../../../providers/AppStateProvider';
import type { ManualRFQPricingService } from './ManualRFQPricingService';
import { getRFQPricingActions } from './ManualRFQPricingSlice';
import { getRFQPricingSelectors } from './selectors';
import type { ManualRFQPricingState } from './types';
import { useManualRFQPricingService } from './useManualRFQPricingService';

interface ManualRFQPricingContextProps {
  /** Stable ID for this instance of Manual RFQ Pricing */
  instanceID: string;
  selectors: ReturnType<typeof getRFQPricingSelectors>;
  actions: ReturnType<typeof getRFQPricingActions>;

  service: ManualRFQPricingService;
  toasts: ReturnType<typeof useToasts<ToastProps>>;
}

const ManualRFQPricingContext = createContext<ManualRFQPricingContextProps | null>(null);
ManualRFQPricingContext.displayName = 'ManualRFQPricingContext';

export function useManualRFQPricingContext() {
  const context = useContext(ManualRFQPricingContext);
  if (context == null) {
    throw new Error('Missing ManualRFQPricingContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export function ManualRFQPricingContextProvider({ children, instanceID }: PropsWithChildren & { instanceID: string }) {
  const dispatch = useDispatch();
  const selectors = useConstant(getRFQPricingSelectors(instanceID));
  const actions = useConstant(getRFQPricingActions(instanceID));

  const state: ManualRFQPricingState | undefined = useAppStateSelector(selectors.selectState);
  const initialState = useConstant(state);

  // Has to be a useEffectOnce ("useOnMount" + no double-run from react strict mode) due to the sensitive contents of the cleanup function
  useEffectOnce(() => {
    if (!initialState) {
      // This case is hit if we navigate to a Manual RFQ Pricing panel just by clicking the tab header, or navigating directly to the URL.
      // There will not have been any prime thunk or similar yet to init the state, so we do that here.
      dispatch(actions.initState(undefined));
    }

    return () => {
      dispatch(actions.destroyState(undefined));
    };
  });

  const toasts = useToasts<ToastProps>();
  const service = useManualRFQPricingService(instanceID, toasts.add);
  useEffect(() => {
    dispatch(actions.setAddToast(toasts.add));
  }, [dispatch, actions, toasts.add]);

  const value = useMemo(() => {
    return {
      selectors,
      instanceID,
      actions,
      service,
      toasts,
    };
  }, [instanceID, selectors, actions, service, toasts]);

  // Before letting the content dependent on the state go ahead and render, we must ensure the state is initialized.
  // Initialization is done in the useEffect above. This early return will only happen for _one_ react render cycle.
  // I prefer to do this rather than type the state as maybe undefined.
  if (!state) {
    return <></>;
  }

  return <ManualRFQPricingContext.Provider value={value}>{children}</ManualRFQPricingContext.Provider>;
}
